/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { empty, Observable, from } from 'rxjs';

import constants from '../../constants';
import * as actionType from '../../store/state/actions';
import { ActionManagerInstance } from '../../actions/manager';
import * as actions from '../../store/state/actions/action-creators';
import { getDataContextName, getTargetFromContainerItemID } from '../../container/container-utils';
import { processDataToDispatchErrorAction, getDirtyDetails } from '../../store/state/epics/util';
import * as StateManager from '../../store/state/state-manager';
import { getResolvedTargetName } from '../../router/routing-manager';
import { getCaseInfoID, isParentAndChildContextHoldingSameItem, promiseHandler } from '../common-helper';
import RemoteCase from '../remote-case';
import type { Action } from '../../store/state/state-manager';
import type { Actionout } from '../../types';

import { CANCEL_ASSIGNMENT } from './action-creator';
import { executeCancelAssignmentRequest } from './async-request';
import { addActionToUpdateParentContextCaseViewMode, getContainerName } from './helper';

export const cancelAssignment = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: Actionout[] = [];

  return action$.pipe(
    ofType(CANCEL_ASSIGNMENT),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((actionInPayload) => {
      const { isModalAction } = actionInPayload;
      if (!actionInPayload.context) {
        actionInPayload.context = getDataContextName(actionInPayload.containerItemID);
      }

      const { context } = actionInPayload;
      const containerName = getContainerName(actionInPayload);
      const target = getResolvedTargetName(containerName, context);
      const { continueExecution } = getDirtyDetails(target);
      if (!continueExecution) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
        return empty();
      }

      actionInPayload.RemoteSystemID = RemoteCase.getRemoteHeaderByContext(actionInPayload.context) ?? undefined;
      return from(executeCancelAssignmentRequest(actionInPayload, { isModalAction }));
    }),
    mergeMap((chainedData) => {
      if (chainedData && chainedData.type === actionType.ERROR) {
        actionsOut.push(processDataToDispatchErrorAction(chainedData));
        return from([actionsOut]);
      }
      const { actionInPayload, response } = chainedData;
      const { containerItemID, isModalAction, isInCreateStage } = actionInPayload;
      if (response?.data.caseInfo) {
        response.data.caseInfo.availableProcesses = response.data.caseInfo.availableProcesses ?? [];
      }

      const target = getTargetFromContainerItemID(containerItemID);
      const context = getDataContextName(containerItemID);
      const payload = {
        target: isModalAction ? `${constants.APP.APP}/${constants.MODAL}` : target,
        containerItemID,
        caseViewMode: isModalAction ? constants.PERFORM_MODE : constants.REVIEW_MODE,
        context,
        actionMgrID,
        key: getCaseInfoID(response),
        skipReleaseLockRequest: false
      };

      if (isInCreateStage) {
        actionsOut.push(actions.postProcess(actions.updateContainerItem, payload));
      }

      if (!isModalAction && isParentAndChildContextHoldingSameItem(containerItemID)) {
        addActionToUpdateParentContextCaseViewMode(actionsOut, {
          childContainerItemID: containerItemID,
          caseViewMode: constants.REVIEW_MODE,
          referencedUsers: response?.data.referencedUsers,
          response
        });
      }

      payload.skipReleaseLockRequest = true;
      actionsOut.push(actions.postProcess(actions.removeContainerItem, payload));
      return from([actionsOut]);
    }),
    map(() => {
      StateManager.dispatch(actionsOut);
      promiseHandler(actionsOut, actionMgrID);
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default cancelAssignment;
